import {
  Alphabet,
  ImageElement,
  RenderSceneUtil,
  StitchElement,
  ThreadColor,
  ViewPortUtil,
  RectangleUtil,
  ThreadRenderOptionsUtil,
  ColorUtil,
  MatrixUtil,
  ElementType,
  StitchElementUtil,
} from "@melco/renderer";
import {
  BaseAction,
  LetteringChanged,
  MultiAction,
  RenderState,
  RevertLetteringChanges,
  SingleColorChanged,
  ViewPortChangeAction,
} from "@melco/renderer/dist/events";
import { FusionMelcoFusionModelsRendererMatrix } from "melco-shared-logic";
import { OriginAndGridElements } from "./useDesignRenderer";
import { ElementConfiguration, Resources } from "./useRenderer";

const defaultBackgroundColor = ColorUtil.createColor(255, 255, 255, 1.0);

const initThreadRenderOptions = () => {
  const threadRenderOptions = ThreadRenderOptionsUtil.createDefault();

  if (/Android|webOS|iPhone|iPad|iPod|Opera Mini/i.test(navigator.userAgent)) {
    threadRenderOptions.directionalLight = true;
  }

  return threadRenderOptions;
};

export type ZoomedTo = "product" | "design";
export type ZoomToFitAction = ZoomedTo | "toggle" | undefined;

export interface FusionRenderState extends RenderState {
  zoomedTo: ZoomedTo;
}

export class FusionRenderStateUtil {
  public static createDefault(): FusionRenderState {
    const renderState: FusionRenderState = {
      scene: RenderSceneUtil.createEmptyScene(),
      viewPort: RectangleUtil.emptyRect(),
      renderOptions: initThreadRenderOptions(),
      zoomedTo: "design",
    };

    renderState.scene.bgColor = defaultBackgroundColor;

    return renderState;
  }

  public static cloneCustomState(c: FusionRenderState): FusionRenderState {
    return { ...c };
  }
}

export class FusionSetBlankPictureAndDesignAction implements BaseAction {
  private blankPicture?: ImageElement;
  private design?: StitchElement;
  private designRendererMatrix?: FusionMelcoFusionModelsRendererMatrix;
  private gridElements?: OriginAndGridElements;

  constructor(
    blankPicture?: ImageElement,
    design?: StitchElement,
    designRendererMatrix?: FusionMelcoFusionModelsRendererMatrix,
    gridElements?: OriginAndGridElements
  ) {
    this.blankPicture = blankPicture;
    this.design = design;
    this.designRendererMatrix = designRendererMatrix;
    this.gridElements = gridElements;
  }

  public modifyState(state: RenderState): RenderState {
    const newState = FusionRenderStateUtil.cloneCustomState(
      state as FusionRenderState
    );

    newState.scene = RenderSceneUtil.cloneScene(newState.scene);
    newState.scene.elements = [];

    if (this.gridElements?.grid) {
      newState.scene.elements.push(this.gridElements.grid);
    }

    if (this.blankPicture) {
      newState.scene.elements.push(this.blankPicture);
    }

    if (this.design) {
      if (this.designRendererMatrix) {
        const { m00, m01, m02, m10, m11, m12 } = this.designRendererMatrix;
        this.design.matrix = MatrixUtil.createMatrix(
          m00!,
          m01!,
          m02!,
          m10!,
          m11!,
          m12!
        );
      }

      newState.scene.elements.push(this.design);

      // initialize color indexes so we don't re-use same colors
      // this is important as we need full control over color handling in Fusion (e.g. with color groups)
      const threadColors: ThreadColor[] = [];

      this.design.subElements = (this.design.subElements ?? []).map(
        (originalSubElement) => {
          const subElement = { ...originalSubElement };

          const colorIndexes: number[] = [];

          subElement.colorsIndexes.forEach((ci) => {
            threadColors.push(this.design!.colors![ci]);
            colorIndexes.push(threadColors.length - 1); // index of last added color
          });

          subElement.colorsIndexes = colorIndexes;

          return subElement;
        }
      );

      this.design.colors = threadColors;
    }

    if (this.gridElements?.origin) {
      newState.scene.elements.push(this.gridElements.origin);
    }

    return newState;
  }
}

export class FusionElementConfigurationChangedAction implements BaseAction {
  private designElement: StitchElement;
  private elementConfiguration: ElementConfiguration;
  private resources: Resources;

  constructor(
    designElement: StitchElement,
    elementConfiguration: ElementConfiguration,
    resources: Resources
  ) {
    this.designElement = designElement;
    this.elementConfiguration = elementConfiguration;
    this.resources = resources;
  }

  public modifyState(state: RenderState): RenderState {
    const actions: BaseAction[] = [];

    const designElementClone = { ...this.designElement };

    const { elements } = this.elementConfiguration;

    (designElementClone.subElements ?? []).forEach((_rendererElement, idx) => {
      const element = elements[idx];

      if (!element) {
        // this can happen in an intermediate state where design and element configuration don't match up
        return;
      }

      const { color, activeColorGroupId, lettering } = element;

      // update colors
      if (color) {
        let threadColor: ThreadColor | undefined;

        if (activeColorGroupId == null) {
          // regular color
          const { red, green, blue } = color;

          threadColor = {
            red,
            green,
            blue,
          };
        } else {
          // grouped color
          const colorGroup = elements[activeColorGroupId].color;

          if (colorGroup) {
            const { red, green, blue } = colorGroup;

            threadColor = {
              red,
              green,
              blue,
            };
          }
        }

        if (threadColor) {
          actions.push(
            new SingleColorChanged(designElementClone, idx, threadColor)
          );
        }
      }

      // update all lettering objects
      if (lettering) {
        const { rendererIndex, text, rfmUrl, isEditable } = lettering;

        // we only want to actually update the lettering if the lettering is editable and the font has been loaded
        // otherwise alphabet will be undefined which causes the renderer to fall back to render initial alphabet
        const alphabet =
          rfmUrl && isEditable
            ? (this.resources[rfmUrl] as Alphabet)
            : undefined;

        if (alphabet) {
          actions.push(
            new LetteringChanged(
              designElementClone,
              rendererIndex,
              text,
              alphabet
            )
          );
        } else {
          actions.push(
            new RevertLetteringChanges(designElementClone, rendererIndex)
          );
        }
      }
    });

    const multiAction = new MultiAction(actions);

    return multiAction.modifyState(state);
  }
}

export class FusionZoomToFitAction implements BaseAction {
  private zoomTo: ZoomToFitAction;
  private width: number;
  private height: number;
  private util: StitchElementUtil;

  constructor(
    zoomTo: ZoomToFitAction,
    width: number,
    height: number,
    util: StitchElementUtil
  ) {
    this.zoomTo = zoomTo;
    this.width = width;
    this.height = height;
    this.util = util;
  }

  public modifyState(state: RenderState): RenderState {
    const newState = FusionRenderStateUtil.cloneCustomState(
      state as FusionRenderState
    );

    if (this.zoomTo === "toggle") {
      newState.zoomedTo =
        (state as FusionRenderState).zoomedTo === "product"
          ? "design"
          : "product";
    }

    const ZOOM_ANIMATION_DURATION_MS = 300;

    const dpi = 0;
    const margin = 32;

    const designElement = newState.scene.elements.find(
      (e) => e?.type === ElementType.DESIGN
    ) as StitchElement;

    const productElement = newState.scene.elements.find(
      (e) => e?.type === ElementType.IMAGE
    ) as ImageElement;

    if (
      (newState.zoomedTo === "design" && !designElement) ||
      (newState.zoomedTo === "product" && !productElement)
    ) {
      return newState;
    }

    const rect =
      newState.zoomedTo === "design"
        ? this.util.calcRectForTransform(
            designElement,
            designElement?.matrix
              ? designElement.matrix
              : MatrixUtil.identityMatrix()
          )
        : productElement.rect!;

    const viewPort = ViewPortUtil.zoomToFit(
      rect,
      this.width,
      this.height,
      dpi,
      margin
    );

    return new ViewPortChangeAction(
      viewPort,
      this.zoomTo ? ZOOM_ANIMATION_DURATION_MS : undefined
    ).modifyState(newState) as FusionRenderState;
  }
}
